import {
  computed,
  Directive,
  type DoCheck,
  forwardRef,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
} from '@angular/core';
import { FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideCircleX } from '@ng-icons/lucide';
import { BrnFormFieldControl } from '@spartan-ng/brain/form-field';
import { ErrorStateMatcher, ErrorStateTracker } from '@spartan-ng/brain/forms';
import { hlm } from '@spartan-ng/helm/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

export const inputVariants = cva(
  'component-medium-medium hover:bg-components-background-neutral-lighter peer hover:border-stroke-neutral-medium file:text-foreground placeholder:text-components-content-neutral-secondary disabled:border-stroke-neutral-medium disabled:text-components-content-neutral-quaternary px-sm py-xs bg-components-background-neutral-lightest disabled:bg-components-background-neutral-lightest read-only:bg-components-background-neutral-lightest read-only:border-stroke-neutral-medium read-only:text-components-content-neutral-quaternary flex h-[44px] w-full min-w-0 rounded-md file:inline-flex file:h-7 file:bg-transparent file:text-sm file:font-medium read-only:pointer-events-none disabled:pointer-events-none',
  {
    variants: {
      variant: {
        noborder: 'border-none focus-visible:outline-none',
        default:
          'outline-feedback-informative-main border-stroke-neutral-medium transition-color focus-visible:outline-components-focus focus-visible:bg-components-background-neutral-lighter border outline-offset-2 file:border-0',
      },
      error: {
        auto: '[&.ng-invalid.ng-touched]:border-danger [&.ng-invalid.ng-touched]:hover:bg-components-background-neutral-lightest [&.ng-invalid.ng-touched]:border-(length:--border-width-sm)',
      },
    },
    defaultVariants: {
      variant: 'default',
      error: 'auto',
    },
  },
);
type InputVariants = VariantProps<typeof inputVariants>;

@Directive({
  selector: '[hlmInput]',
  host: {
    '[class]': '_computedClass()',
  },
  providers: [
    {
      provide: BrnFormFieldControl,
      useExisting: forwardRef(() => HlmInput),
    },
    provideIcons({ lucideCircleX }),
  ],
})
export class HlmInput implements BrnFormFieldControl, DoCheck {
  public readonly error = input<InputVariants['error']>('auto');
  public readonly variant = input<InputVariants['variant']>('default');

  protected readonly _state = linkedSignal(() => ({ error: this.error() }));

  public readonly userClass = input<ClassValue>('', { alias: 'class' });
  protected readonly _computedClass = computed(() =>
    hlm(inputVariants({ error: this._state().error, variant: this.variant() }), this.userClass()),
  );

  private readonly _injector = inject(Injector);
  private readonly _additionalClasses = signal<ClassValue>('');

  public readonly ngControl: NgControl | null = this._injector.get(NgControl, null);

  private readonly _errorStateTracker: ErrorStateTracker;

  private readonly _defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private readonly _parentForm = inject(NgForm, { optional: true });
  private readonly _parentFormGroup = inject(FormGroupDirective, { optional: true });

  public readonly errorState = computed(() => this._errorStateTracker.errorState());

  constructor() {
    this._errorStateTracker = new ErrorStateTracker(
      this._defaultErrorStateMatcher,
      this.ngControl,
      this._parentFormGroup,
      this._parentForm,
    );
  }

  ngDoCheck() {
    this._errorStateTracker.updateErrorState();
  }

  setError(error: InputVariants['error']) {
    this._state.set({ error });
  }

  setClass(classes: string): void {
    this._additionalClasses.set(classes);
  }
}
